import { Circle, generateMapProps } from './generate'
import { useEffect, useRef, useState } from 'react'
import { useContextSelectedMotifs } from '../../../contexts/selectedMotifContext'
import styled from 'styled-components'
import { EXPORT_SIZE, useContextMotifGenerator } from '../../../contexts/motifGeneratorContext'
import { PositionXY } from '../../../types/PositionXY'
import { useThrottleFn } from '@react-cmpt/use-throttle'
import { isAndroid, isIOS } from 'react-device-detect'
import { JSONExportPlane, Dimensions, MotifGeneratorType } from '../types'
import { getPagePositionXY } from '../../../utils/getPagePositionXY'
import { ExportLayoutModalResponse } from '../ExportLayoutModal'
import { isSafari as _isSafari, SAFARI_MAX_CANVAS_SIZE } from '../../../utils/safariCanvasUtils'
const isAppleDevice = _isSafari || isIOS

const RESOLUTION_MULTIPLIER = 2
const MAX_NON_SAFARI_PREVIEW_MULTIPLIER = 3
const MAX_NON_SAFARI_PREVIEW_CANVAS_SIZE = 16384
const NON_SAFARI_EXPORT_MULTIPLIER = 3
const EXPORT_SUPERSAMPLING_MULTIPLIER = 3
const SAFARI_EXPORT_SIZE = Math.min(
  EXPORT_SIZE * NON_SAFARI_EXPORT_MULTIPLIER,
  SAFARI_MAX_CANVAS_SIZE,
)
const MIN_SCALE = 0.5
const MAX_SCALE = 1.2
const TILE_MULTIPLIER = 3

const Container = styled.canvas<{ $isAppleDevice?: boolean }>`
  background-color: ${({ theme }) => theme.colors.white};
  width: min(100%, 100vw) !important;
  max-width: 100vw;
  aspect-ratio: 1 / 1;
  height: auto;
  display: block;
  margin: auto;
  position: relative;
  z-index: 0;

  & + canvas {
    position: absolute;
    top: 0;
    left: 0;
    z-index: -1;
  }
`

type Props = {
  exportResponse?: ExportLayoutModalResponse
  onExportComplete?: () => void
}

export const MapTypeGenerator = ({ exportResponse, onExportComplete }: Props) => {
  const { selectedMotifs } = useContextSelectedMotifs()
  const {
    setExportInvoked,
    startExport,
    setStartExport,
    exportGeneratorCoordinatesToJSON,
    handleExport,
    handleExportCanvas,
    isRegenerated,
  } = useContextMotifGenerator()

  const containerRef = useRef<HTMLCanvasElement | null>(null)
  const canvasToExportRef = useRef<HTMLCanvasElement>(null)
  const [motifImages, setMotifImages] = useState<HTMLImageElement[]>([])
  const dragging = useRef<boolean>(false)
  const lastPos = useRef<PositionXY>({ x: 0, y: 0 })
  const [circles, setCircles] = useState<Circle[]>()
  const [scale, setScale] = useState(1)
  const exportInProgress = useRef<boolean>(false)
  const nonSafariPreviewMultiplier = Math.min(
    MAX_NON_SAFARI_PREVIEW_MULTIPLIER,
    Math.max(RESOLUTION_MULTIPLIER, window.devicePixelRatio || 1),
  )
  const maxPreviewCanvasSize = isAppleDevice
    ? SAFARI_MAX_CANVAS_SIZE
    : MAX_NON_SAFARI_PREVIEW_CANVAS_SIZE
  const baseViewportSize = window.innerWidth < 1000 ? 1000 : window.innerWidth

  const getInitialCanvasSize = () => {
    if (isAppleDevice) {
      const base = baseViewportSize * Math.max(1, window.devicePixelRatio)
      const size = Math.min(base, maxPreviewCanvasSize)
      return { width: size, height: size }
    } else {
      const base = baseViewportSize * nonSafariPreviewMultiplier
      const maxBase = maxPreviewCanvasSize / TILE_MULTIPLIER
      const size = Math.min(base, maxBase)
      return { width: size, height: size }
    }
  }
  const canvasSize = useRef<Dimensions>(getInitialCanvasSize())
  const jsonCoordinates = useRef<JSONExportPlane>({
    clipPlane: undefined,
    entries: [],
  })

  const logicalSize = baseViewportSize * (TILE_MULTIPLIER / RESOLUTION_MULTIPLIER)

  const loadImages = async () => {
    if (selectedMotifs.length > 0) {
      const imageUrls = selectedMotifs
        .map(m => m.vectorizedUrl)
        .filter((url): url is string => !!url)
      const images = await Promise.all(
        imageUrls.map(url => {
          const img = new Image()
          img.crossOrigin = 'anonymous'
          return new Promise<HTMLImageElement | null>(resolve => {
            img.onload = () => resolve(img)
            img.onerror = error => {
              console.error(`Failed to load image: ${url}`, error)
              resolve(null)
            }
            img.src = url
          })
        }),
      )
      setMotifImages(images.filter((img): img is HTMLImageElement => img !== null))
    } else {
      setMotifImages([])
    }
  }
  useEffect(() => void loadImages(), [selectedMotifs])

  useEffect(() => {
    if (
      !startExport ||
      !containerRef.current ||
      !canvasToExportRef.current ||
      !circles ||
      exportInProgress.current
    )
      return

    exportInProgress.current = true
    const displayCanvas = containerRef.current!

    // Export JSON first
    exportGeneratorCoordinatesToJSON({
      props: {
        type: MotifGeneratorType.MAP,
        selectedMotifs,
        circles,
        canvasSize: { width: displayCanvas.width, height: displayCanvas.height },
      },
      planes: [jsonCoordinates.current],
      canvasSize: {
        width: displayCanvas.width,
        height: displayCanvas.height,
      },
    })

    const isTiled = exportResponse === ExportLayoutModalResponse.TILES

    const exportSize = isAppleDevice
      ? SAFARI_EXPORT_SIZE
      : EXPORT_SIZE * NON_SAFARI_EXPORT_MULTIPLIER
    canvasToExportRef.current.width = exportSize
    canvasToExportRef.current.height = exportSize
    const exportCtx = canvasToExportRef.current.getContext('2d')!
    exportCtx.imageSmoothingEnabled = true
    exportCtx.imageSmoothingQuality = 'high'
    exportCtx.fillStyle = '#fff'
    exportCtx.fillRect(0, 0, exportSize, exportSize)

    if (isAppleDevice) {
      if (isTiled) {
        drawTiledExport(exportCtx, exportSize, scale, circles)
      } else {
        drawSingleCenteredExport(exportCtx, exportSize, scale, circles)
      }
    } else {
      const renderSize = exportSize * EXPORT_SUPERSAMPLING_MULTIPLIER
      const circleSpace = logicalSize

      const tempCanvas = document.createElement('canvas')
      tempCanvas.width = renderSize
      tempCanvas.height = renderSize
      const tempCtx = tempCanvas.getContext('2d')!
      tempCtx.imageSmoothingEnabled = true
      tempCtx.imageSmoothingQuality = 'high'
      tempCtx.fillStyle = '#fff'
      tempCtx.fillRect(0, 0, renderSize, renderSize)

      if (isTiled) {
        const tiledMultiplier = renderSize / circleSpace
        drawPattern(tempCtx, tiledMultiplier, scale, circles)
        exportCtx.drawImage(tempCanvas, 0, 0, renderSize, renderSize, 0, 0, exportSize, exportSize)
      } else {
        const singleMultiplier = renderSize / (circleSpace / 2)
        const translateOffset = -(circleSpace / 4) * singleMultiplier
        tempCtx.save()
        tempCtx.translate(translateOffset, translateOffset)
        drawPattern(tempCtx, singleMultiplier, scale, circles)
        tempCtx.restore()
        exportCtx.drawImage(tempCanvas, 0, 0, renderSize, renderSize, 0, 0, exportSize, exportSize)
      }
    }

    if (isIOS || isAndroid) {
      handleExportCanvas(canvasToExportRef.current!)
    } else {
      handleExport(canvasToExportRef.current!).catch(console.error)
    }
    exportInProgress.current = false
    setExportInvoked(false)
    setStartExport(false)
    onExportComplete?.()
  }, [startExport, onExportComplete, circles, scale, exportResponse])

  useEffect(() => {
    if (
      motifImages.length === selectedMotifs.length &&
      motifImages.length > 0 &&
      containerRef?.current
    ) {
      const displayWidth = logicalSize
      const displayHeight = logicalSize

      const circles = generateMapProps(selectedMotifs, displayWidth, displayHeight)
      jsonCoordinates.current.entries = circles
        .filter(c => c.idx >= 0 && c.idx < selectedMotifs.length)
        .map(c => Object.assign(c, { size: 2 * c.r, id: selectedMotifs[c.idx].id }))
      setCircles(circles)
    }
  }, [containerRef, isRegenerated, selectedMotifs, motifImages])

  useEffect(() => {
    if (circles && motifImages.length > 0) {
      const validCircles = circles.filter(
        c => c.idx >= 0 && c.idx < motifImages.length && c.idx < selectedMotifs.length,
      )
      if (validCircles.length > 0) {
        draw(validCircles)
      }
    }
  }, [circles, scale, motifImages, selectedMotifs])

  const drawPattern = (
    ctx: CanvasRenderingContext2D,
    resolutionMultiplier: number,
    currentScale: number,
    circlesToDraw: Circle[],
  ) => {
    circlesToDraw.forEach(c => {
      if (c.idx < 0 || c.idx >= motifImages.length || c.idx >= selectedMotifs.length) {
        console.warn(
          `Invalid index ${c.idx}, available motifs: ${Math.min(
            motifImages.length,
            selectedMotifs.length,
          )}`,
        )
        return
      }

      const img = motifImages[c.idx]

      if (!img) {
        console.warn(`Image not found at index ${c.idx}`)
        return
      }

      ctx.save()
      ctx.translate(c.x * resolutionMultiplier, c.y * resolutionMultiplier)
      ctx.rotate(c.rotation)

      const imageSize = 2 * resolutionMultiplier * c.r * currentScale
      ctx.drawImage(img, -imageSize / 2, -imageSize / 2, imageSize, imageSize)

      ctx.restore()
    })
  }

  const drawTiledExport = (
    ctx: CanvasRenderingContext2D,
    targetSize: number,
    currentScale: number,
    circlesToDraw: Circle[],
  ) => {
    const tiledMultiplier = targetSize / logicalSize
    drawPattern(ctx, tiledMultiplier, currentScale, circlesToDraw)
  }

  const drawSingleCenteredExport = (
    ctx: CanvasRenderingContext2D,
    targetSize: number,
    currentScale: number,
    circlesToDraw: Circle[],
  ) => {
    const singleMultiplier = (2 * targetSize) / logicalSize
    const offset = -(logicalSize * singleMultiplier - targetSize) / 2
    ctx.save()
    ctx.translate(offset, offset)
    drawPattern(ctx, singleMultiplier, currentScale, circlesToDraw)
    ctx.restore()
  }

  const draw = (circles: Circle[]) => {
    if (!containerRef.current) return

    const ctx = containerRef.current.getContext('2d')
    if (!ctx || !containerRef.current) return

    ctx.imageSmoothingEnabled = true
    ctx.imageSmoothingQuality = 'high'

    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, containerRef.current.width, containerRef.current.height)

    const previewMultiplier = containerRef.current.width / logicalSize
    drawPattern(ctx, previewMultiplier, scale, circles)
  }

  const handleMouseDown = (event: React.MouseEvent) => {
    event.preventDefault()
    onStartEvent(event.pageX, event.pageY)
  }

  const handleTouchStart = (event: React.TouchEvent) => {
    onStartEvent(event.touches[0].pageX, event.touches[0].pageY)
  }

  const onStartEvent = (x: number, y: number) => {
    lastPos.current.x = x
    lastPos.current.y = y
    dragging.current = true
  }

  const handleMouseUp = (event: React.MouseEvent | React.TouchEvent) => {
    event.preventDefault()
    dragging.current = false
  }

  const handleMouseMove = (event: React.MouseEvent | React.TouchEvent): void => {
    if (dragging.current && circles) {
      const pageXY = getPagePositionXY(event)
      const moveX = lastPos.current.x - pageXY.x
      const moveY = lastPos.current.y - pageXY.y

      lastPos.current = pageXY

      setCircles(
        circles.map(c =>
          Object.assign(c, {
            rotation: c.rotation - (moveX / window.innerHeight) * 2,
          }),
        ),
      )

      let newScale = scale + (moveY / window.innerHeight) * 2
      newScale = Math.max(newScale, MIN_SCALE)
      newScale = Math.min(newScale, MAX_SCALE)

      setScale(newScale)
    }
  }

  const { callback } = useThrottleFn(handleMouseMove, 40)

  return (
    <>
      <Container
        ref={containerRef}
        onMouseDown={handleMouseDown}
        onMouseMove={callback}
        onMouseUp={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={callback}
        onTouchEnd={handleMouseUp}
        width={canvasSize.current.width}
        height={canvasSize.current.height}
        $isAppleDevice={isAppleDevice}
        style={isAppleDevice ? { width: '100%', height: 'auto' } : undefined}
      />
      <canvas
        ref={canvasToExportRef}
        width={isAppleDevice ? SAFARI_EXPORT_SIZE : EXPORT_SIZE * NON_SAFARI_EXPORT_MULTIPLIER}
        height={isAppleDevice ? SAFARI_EXPORT_SIZE : EXPORT_SIZE * NON_SAFARI_EXPORT_MULTIPLIER}
      />
    </>
  )
}

export default MapTypeGenerator
